import { Box, Typography } from '../../../components/ui';
import type { SOPRunbookContent } from '../../../types/knowledgeBase';
import { StepCard } from './StepCard';
import './SOPRunbookView.css';

interface SOPRunbookViewProps {
  content: SOPRunbookContent;
}

export function SOPRunbookView({ content }: SOPRunbookViewProps) {
  return (
    <Box className="sop-runbook-view">
      {/* Objective */}
      <Box className="sop-runbook-view__section sop-runbook-view__section--objective" component="section" aria-labelledby="objective-title">
        <Box className="sop-runbook-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="sop-runbook-view__icon sop-runbook-view__icon--objective"
            aria-hidden="true"
          >
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
            <circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="2" />
            <circle cx="10" cy="10" r="1" fill="currentColor" />
          </svg>
          <Typography id="objective-title" variant="h6" className="sop-runbook-view__section-title">
            Objective
          </Typography>
        </Box>
        <Typography variant="body1" className="sop-runbook-view__section-content">
          {content.objective}
        </Typography>
      </Box>

      {/* Prerequisites */}
      <Box className="sop-runbook-view__section" component="section" aria-labelledby="prerequisites-title">
        <Box className="sop-runbook-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="sop-runbook-view__icon"
            aria-hidden="true"
          >
            <path
              d="M5 10l3 3 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          </svg>
          <Typography id="prerequisites-title" variant="h6" className="sop-runbook-view__section-title">
            Prerequisites
          </Typography>
        </Box>
        <Box className="sop-runbook-view__prerequisites" role="list" aria-label="Prerequisites checklist">
          {content.prerequisites.map((prereq, index) => (
            <Box key={index} className="sop-runbook-view__prereq" role="listitem">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="sop-runbook-view__prereq-check"
                aria-hidden="true"
              >
                <path
                  d="M3 8l3 3 7-7"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="sop-runbook-view__prereq-text">{prereq}</span>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Procedure Steps */}
      <Box className="sop-runbook-view__section" component="section" aria-labelledby="procedure-steps-title">
        <Box className="sop-runbook-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="sop-runbook-view__icon"
            aria-hidden="true"
          >
            <path
              d="M6 5h8M6 10h8M6 15h4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="3" cy="5" r="1" fill="currentColor" />
            <circle cx="3" cy="10" r="1" fill="currentColor" />
            <circle cx="3" cy="15" r="1" fill="currentColor" />
          </svg>
          <Typography id="procedure-steps-title" variant="h6" className="sop-runbook-view__section-title">
            Procedure Steps
          </Typography>
        </Box>
        <Box className="sop-runbook-view__steps" role="list" aria-label="Procedure steps">
          {content.procedureSteps.map((step) => (
            <StepCard key={step.id} step={step} />
          ))}
        </Box>
      </Box>

      {/* Verification */}
      <Box className="sop-runbook-view__section sop-runbook-view__section--verification" component="section" aria-labelledby="verification-title">
        <Box className="sop-runbook-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="sop-runbook-view__icon sop-runbook-view__icon--verification"
            aria-hidden="true"
          >
            <path
              d="M5 10l3 3 7-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" />
          </svg>
          <Typography id="verification-title" variant="h6" className="sop-runbook-view__section-title">
            Verification
          </Typography>
        </Box>
        <Typography variant="body1" className="sop-runbook-view__section-content">
          {content.verification}
        </Typography>
      </Box>

      {/* Rollback Procedure (if exists) */}
      {content.rollbackProcedure && (
        <Box className="sop-runbook-view__section sop-runbook-view__section--rollback" component="section" aria-labelledby="rollback-title">
          <Box className="sop-runbook-view__section-header">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="sop-runbook-view__icon sop-runbook-view__icon--rollback"
              aria-hidden="true"
            >
              <path
                d="M4 10h12M4 10l4-4M4 10l4 4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography id="rollback-title" variant="h6" className="sop-runbook-view__section-title">
              Rollback Procedure
            </Typography>
          </Box>
          <Typography variant="body1" className="sop-runbook-view__section-content">
            {content.rollbackProcedure}
          </Typography>
        </Box>
      )}

      {/* Emergency Contacts */}
      <Box className="sop-runbook-view__section sop-runbook-view__section--contacts" component="section" aria-labelledby="emergency-contacts-title">
        <Box className="sop-runbook-view__section-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="sop-runbook-view__icon sop-runbook-view__icon--contacts"
            aria-hidden="true"
          >
            <circle cx="10" cy="6" r="3" stroke="currentColor" strokeWidth="2" />
            <path
              d="M4 17v-1a4 4 0 014-4h4a4 4 0 014 4v1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <Typography id="emergency-contacts-title" variant="h6" className="sop-runbook-view__section-title">
            Emergency Contacts
          </Typography>
        </Box>
        <Box className="sop-runbook-view__contacts" role="list" aria-label="Emergency contacts">
          {content.emergencyContacts.map((contact) => (
            <Box key={contact.id} className="sop-runbook-view__contact" role="listitem">
              <Box className="sop-runbook-view__contact-avatar" aria-hidden="true">
                {contact.name.charAt(0).toUpperCase()}
              </Box>
              <Box className="sop-runbook-view__contact-info">
                <span className="sop-runbook-view__contact-name">{contact.name}</span>
                <span className="sop-runbook-view__contact-role">{contact.role}</span>
                <span className="sop-runbook-view__contact-value">{contact.contact}</span>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
